/**
 * The signed-in user's session.
 *
 * Replaces the Vuex `login` module, which held the session id, CSRF token,
 * account number, unique id, a `is2faVerified` flag, and the full user record,
 * persisted into `localStorage` via `vuex-persistedstate` — i.e. patient-facing
 * identity data written to disk and a second-factor flag the client could set
 * for itself.
 *
 * Here the session is derived from two sources, neither of which the client
 * can forge:
 *
 *  - the **id_token**, for rendering (name, organization, role);
 *  - **GET /api/v1/me**, for authority (the permissions the server will
 *    actually honour).
 *
 * Nothing is persisted to `localStorage`. Reloading the page re-derives the
 * session from the refresh token, which is per-tab and rotates on use.
 */

import { defineStore } from 'pinia';
import oidc from '@/auth/oidcClient';
import api from '@/api2/client';
// A cycle (patientContext imports this module), and a harmless one: neither
// module touches the other until a getter or an action runs.
import { usePatientContextStore } from '@/stores/patientContext';

/**
 * Bumped for every `GET /me` started, so only the newest one writes the
 * session. Module-level rather than state so that `$reset()` cannot rewind it
 * and let an old response match a new number.
 */
let requestSeq = 0;

/** The chart in view, or null — read lazily, and never fatal to a check. */
function openChartId() {
  try {
    return usePatientContextStore().patientId ?? null;
  } catch (e) {
    return null;
  }
}

/**
 * Whether a single-chart emergency incident lends `permission` on this chart
 * (decision D8). `GET /me` lists them apart from `permissions`, because they
 * hold on that one chart and nowhere else: offered while it is the chart in
 * view, so the person the incident was opened for can reach the sections it
 * lent, and never on any other. The API decides every request regardless.
 */
function lentOnChart(state, permission, patientId) {
  if (patientId === null || patientId === undefined) return false;

  const charts = Array.isArray(state.me?.break_glass_charts) ? state.me.break_glass_charts : [];
  const now = Date.now();

  return charts.some((chart) => String(chart.patient_id) === String(patientId)
    && Array.isArray(chart.permissions)
    && chart.permissions.includes(permission)
    && !(chart.expires_at && Date.parse(chart.expires_at) <= now));
}

/** Held everywhere, or lent on the chart in view. */
function holds(state, permission) {
  return state.permissions.includes(permission) || lentOnChart(state, permission, openChartId());
}

export const useSessionStore = defineStore('session', {
  state: () => ({
    /** Claims from the id_token — display only. */
    identity: null,
    /** The authoritative profile from the API. */
    me: null,
    /** Permission codes the server will honour for this user. */
    permissions: [],
    /** Scopes the access token actually carries. */
    scopes: [],
    loading: false,
    error: null,
    /**
     * The `GET /me` in flight, so concurrent callers join it instead of racing
     * it. A promise, not data — nothing reads it but `hydrate`.
     */
    inflight: null,
    /**
     * Whose session the in-flight load answers for (the token's `sub`). A
     * caller for anybody else must not join it: they would inherit the other
     * person's permissions.
     */
    inflightSubject: null,
  }),

  getters: {
    isAuthenticated: () => oidc.isAuthenticated(),

    userId: (state) => state.me?.id ?? state.identity?.user_id ?? null,

    organizationId: (state) => state.me?.organization?.id ?? state.identity?.org_id ?? null,

    organizationName: (state) => state.me?.organization?.name ?? state.identity?.org_name ?? '',

    /**
     * The organization's account number — the legacy `accountnumber`.
     *
     * Falls back to the surrogate id, because a legacy call site that sends an
     * empty account number reads as "no organization" and several of them
     * refuse outright rather than proceed. Both forms identify the same tenant
     * to the bridge, which does not send either: the token already says which
     * organization the caller is in.
     */
    organizationNumber: (state) => state.me?.organization?.number
      ?? state.identity?.org_number
      ?? state.me?.organization?.id
      ?? null,

    accountType: (state) => state.me?.account_type ?? state.identity?.account_type ?? null,

    displayName: (state) => state.me?.preferred_name
      || state.me?.full_name
      || state.identity?.name
      || state.identity?.preferred_username
      || '',

    isPatient: (state) => Boolean(state.me?.patient_id),

    needsOnboarding: (state) => Boolean(state.me?.needs_onboarding),

    roles: (state) => state.me?.roles ?? state.identity?.roles ?? [],

    /**
     * Whether the UI should offer an action.
     *
     * This is a *UX* decision, not a security one — the API re-checks every
     * permission server-side. In the legacy app the reverse was true: 56 of 60
     * permissions were enforced only by hiding a button.
     *
     * Includes what a single-chart emergency incident lends, while that chart
     * is the one in view (`lentOnChart`).
     */
    can: (state) => (permission) => holds(state, permission),

    canAny: (state) => (...permissions) => permissions.some((p) => holds(state, p)),

    canAll: (state) => (...permissions) => permissions.every((p) => holds(state, p)),

    /** Whether a single-chart emergency incident lends this on that chart. */
    lentOnChart: (state) => (permission, patientId) => lentOnChart(state, permission, patientId),

    /** True while acting as another user via an act-as token. */
    isImpersonating: (state) => Boolean(state.me?.impersonating),

    /** The human behind an act-as session, when there is one. */
    actingUser: (state) => state.me?.impersonating?.actor_name ?? null,

    /** Who they are acting as. */
    actingAsUser: (state) => state.me?.impersonating?.acting_as_name ?? null,
  },

  actions: {
    /**
     * Load the session after sign-in or on app boot.
     *
     * A hard reload asks for this from several places at once — the router
     * guard, the privileges store, the legacy privileges shim — and each used
     * to issue its own `GET /me`. A caller that arrives while a load is running
     * now joins it. The join lasts only while the request is in flight: a call
     * made after it settles, such as the reload after a change of session,
     * fetches again.
     */
    async hydrate() {
      if (!oidc.isAuthenticated()) {
        this.$reset();
        // Nothing was asked, so nothing loaded — and `loadedKey` stays empty.
        // Recorded so "not signed in" is distinguishable from "holds nothing".
        this.error = 'missing_identity';
        return null;
      }

      const subject = oidc.getIdentity()?.sub ?? null;

      if (this.inflight && this.inflightSubject === subject) return this.inflight;

      requestSeq += 1;
      const run = this.fetchSession(requestSeq);

      this.inflight = run;
      this.inflightSubject = subject;

      try {
        return await run;
      } finally {
        if (this.inflight === run) {
          this.inflight = null;
          this.inflightSubject = null;
        }
      }
    },

    /**
     * The request itself. Split out so `hydrate` can hand the same promise to
     * every concurrent caller; never call this directly.
     *
     * A load overtaken by one for a different subject still settles, but does
     * not write: whichever request answers last must not decide whose
     * permissions the UI shows.
     */
    async fetchSession(seq = requestSeq) {
      this.loading = true;
      this.error = null;

      try {
        const identity = oidc.getIdentity();

        const me = await api.get('/me');

        if (seq !== requestSeq) return me;

        this.identity = identity;
        this.me = me;
        this.permissions = me.permissions || [];
        this.scopes = me.scopes || [];

        // A patient's own chart is the only sensible default, and without it
        // every component keyed on the current patient reads null. Deliberately
        // not awaited into the failure path below: a chart that cannot be
        // loaded should not make the session itself look unauthenticated.
        try {
            const { usePatientContextStore } = await import('@/stores/patientContext');
            await usePatientContextStore().initialise();
        } catch (e) {
            /* the session is valid regardless; the chart picker still works */
        }

        return me;
      } catch (e) {
        if (seq !== requestSeq) throw e;

        this.error = e.message;
        // A failure here means the token is not usable; treat it as signed out
        // rather than leaving the app in a half-authenticated state — that
        // in-between state is exactly what the legacy 2FA flow got wrong.
        if (e.isAuthError) {
          this.$reset();
        }
        throw e;
      } finally {
        if (seq === requestSeq) this.loading = false;
      }
    },

    async login(returnTo) {
      await oidc.beginLogin({ returnTo });
    },

    async logout() {
      this.$reset();
      await oidc.logout();
    },
  },
});

export default useSessionStore;
